import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, Phone, User, MessageSquare, Tag } from 'lucide-react';
import { useContactSubmit } from '../hooks/useContactSubmit';

const InputWithIcon = ({ icon: Icon, ...rest }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
    <Input {...rest} className="pl-10 bg-slate-800 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400" />
  </div>
);

const CONTACT_CATEGORIES = [
  'General Inquiry', 'Custom Build', 'Repair', 'Cleaning', 'FFL Transfer', 'Other',
];

const ContactForm = () => {
  const { formData, isSubmitting, handleInputChange, handleCategoryChange, handleSubmit } = useContactSubmit();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
        <InputWithIcon
          icon={User} id="name" name="name" type="text" required
          value={formData.name} onChange={handleInputChange}
          disabled={isSubmitting} placeholder="John Doe"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
        <InputWithIcon
          icon={Mail} id="email" name="email" type="email" required
          value={formData.email} onChange={handleInputChange}
          disabled={isSubmitting} placeholder="john@example.com"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
        <InputWithIcon
          icon={Phone} id="phone" name="phone" type="tel"
          value={formData.phone} onChange={handleInputChange}
          disabled={isSubmitting} placeholder="(555) 123-4567"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category" className="text-gray-300">What can we help you with? *</Label>
        <div className="relative">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 z-10" />
          <Select required value={formData.category} onValueChange={handleCategoryChange} disabled={isSubmitting}>
            <SelectTrigger className="pl-10 bg-slate-800 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {CONTACT_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} className="text-white hover:bg-slate-700">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-gray-300">Message *</Label>
        <div className="relative">
          <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
          <Textarea
            id="message" name="message" required rows={5}
            value={formData.message} onChange={handleInputChange} disabled={isSubmitting}
            className="pl-10 bg-slate-800 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400 resize-none"
            placeholder="Tell us how we can help..."
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 transition-colors duration-200"
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  );
};

export default ContactForm;
